import React, { useState } from "react";
import {
  Box, Button, Card, Chip, Collapse, Divider, MenuItem, Stack, TextField, Typography, InputAdornment,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";

// Filter panel. Row 1: search, status, has-subjects, more-filters toggle,
// clear. Row 2 (collapsible): school-grade, enrolled-grade, subject,
// study-mode, location. Selected non-default filters render as removable
// chips underneath so the admin can see and undo what's applied at a glance.
export type FilterState = {
  search: string;
  student_status: string;          // 'active' | 'dropped' | ''
  has_subjects: "" | "true" | "false";
  school_grade_id: number[];
  enrolled_grade_id: number[];
  subject_id: number[];
  study_mode: string[];            // 'in_person' | 'online' | ...
  location_id: number[];
};

type Option = { id: number; label: string };

type Props = {
  value: FilterState;
  onChange: (v: FilterState) => void;
  onClear: () => void;
  loading: boolean;
  gradeOptions: Option[];
  subjectOptions: Option[];
  locationOptions: Option[];
  studyModeOptions: { value: string; label: string }[];
};

const defaultFilter: FilterState = {
  search: "", student_status: "active", has_subjects: "",
  school_grade_id: [], enrolled_grade_id: [], subject_id: [],
  study_mode: [], location_id: [],
};

const humanMode = (m: string) =>
  m === "in_person" ? "In Person" : m === "online" ? "Online" : m === "hybrid" ? "Hybrid" : m;

export default function FilterPanel({
  value, onChange, onClear, loading,
  gradeOptions, subjectOptions, locationOptions, studyModeOptions,
}: Props) {
  const [open, setOpen] = useState(false);

  const set = <K extends keyof FilterState>(k: K, v: FilterState[K]) =>
    onChange({ ...value, [k]: v });

  // Chips for currently-applied non-default filters.
  const chips: { key: string; label: string; onDelete: () => void }[] = [];
  if (value.search) chips.push({
    key: "search", label: `Search: “${value.search}”`, onDelete: () => set("search", ""),
  });
  if (value.student_status && value.student_status !== defaultFilter.student_status) chips.push({
    key: "status", label: `Status: ${value.student_status}`, onDelete: () => set("student_status", defaultFilter.student_status),
  });
  if (value.has_subjects) chips.push({
    key: "hs", label: value.has_subjects === "true" ? "Has subjects" : "No subjects",
    onDelete: () => set("has_subjects", ""),
  });
  value.school_grade_id.forEach((id) => chips.push({
    key: `sg${id}`, label: `School Grade: ${gradeOptions.find((g) => g.id === id)?.label || id}`,
    onDelete: () => set("school_grade_id", value.school_grade_id.filter((x) => x !== id)),
  }));
  value.enrolled_grade_id.forEach((id) => chips.push({
    key: `eg${id}`, label: `Enrolled Grade: ${gradeOptions.find((g) => g.id === id)?.label || id}`,
    onDelete: () => set("enrolled_grade_id", value.enrolled_grade_id.filter((x) => x !== id)),
  }));
  value.subject_id.forEach((id) => chips.push({
    key: `sub${id}`, label: `Subject: ${subjectOptions.find((s) => s.id === id)?.label || id}`,
    onDelete: () => set("subject_id", value.subject_id.filter((x) => x !== id)),
  }));
  value.study_mode.forEach((m) => chips.push({
    key: `mode${m}`, label: `Mode: ${humanMode(m)}`,
    onDelete: () => set("study_mode", value.study_mode.filter((x) => x !== m)),
  }));
  value.location_id.forEach((id) => chips.push({
    key: `loc${id}`, label: `Location: ${locationOptions.find((l) => l.id === id)?.label || id}`,
    onDelete: () => set("location_id", value.location_id.filter((x) => x !== id)),
  }));

  return (
    <Card variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center">
        <TextField
          size="small" fullWidth
          placeholder="Search student name or reference ID"
          value={value.search}
          onChange={(e) => set("search", e.target.value)}
          InputProps={{
            startAdornment: (<InputAdornment position="start"><SearchOutlinedIcon fontSize="small" /></InputAdornment>),
          }}
          sx={{ maxWidth: { md: 380 } }}
          disabled={loading}
        />
        <TextField select size="small" label="Student Status" value={value.student_status}
          onChange={(e) => set("student_status", e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="dropped">Dropped</MenuItem>
        </TextField>
        <TextField select size="small" label="Subject Enrolment" value={value.has_subjects}
          onChange={(e) => set("has_subjects", e.target.value as any)} sx={{ minWidth: 180 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="true">Has subjects</MenuItem>
          <MenuItem value="false">No subjects</MenuItem>
        </TextField>
        <Button variant="outlined" size="small" startIcon={<TuneOutlinedIcon />}
          onClick={() => setOpen((x) => !x)}>
          {open ? "Hide filters" : "More filters"}
        </Button>
        <Button size="small" onClick={onClear}>Clear</Button>
      </Stack>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Divider sx={{ my: 2 }} />
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} flexWrap="wrap">
          <TextField select size="small" label="School Grade"
            SelectProps={{ multiple: true, renderValue: (v: any) =>
              (v as number[]).map((id) => gradeOptions.find((g) => g.id === id)?.label || id).join(", ") }}
            value={value.school_grade_id}
            onChange={(e) => set("school_grade_id", e.target.value as any as number[])}
            sx={{ minWidth: 180 }}>
            {gradeOptions.map((g) => <MenuItem key={g.id} value={g.id}>{g.label}</MenuItem>)}
          </TextField>

          <TextField select size="small" label="Enrolled Grade"
            SelectProps={{ multiple: true, renderValue: (v: any) =>
              (v as number[]).map((id) => gradeOptions.find((g) => g.id === id)?.label || id).join(", ") }}
            value={value.enrolled_grade_id}
            onChange={(e) => set("enrolled_grade_id", e.target.value as any as number[])}
            sx={{ minWidth: 180 }}>
            {gradeOptions.map((g) => <MenuItem key={g.id} value={g.id}>{g.label}</MenuItem>)}
          </TextField>

          <TextField select size="small" label="Subject"
            SelectProps={{ multiple: true, renderValue: (v: any) =>
              (v as number[]).map((id) => subjectOptions.find((s) => s.id === id)?.label || id).join(", ") }}
            value={value.subject_id}
            onChange={(e) => set("subject_id", e.target.value as any as number[])}
            sx={{ minWidth: 220 }}>
            {subjectOptions.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
          </TextField>

          <TextField select size="small" label="Study Mode"
            SelectProps={{ multiple: true, renderValue: (v: any) => (v as string[]).map(humanMode).join(", ") }}
            value={value.study_mode}
            onChange={(e) => set("study_mode", e.target.value as any as string[])}
            sx={{ minWidth: 180 }}>
            {studyModeOptions.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </TextField>

          <TextField select size="small" label="Location"
            SelectProps={{ multiple: true, renderValue: (v: any) =>
              (v as number[]).map((id) => locationOptions.find((l) => l.id === id)?.label || id).join(", ") }}
            value={value.location_id}
            onChange={(e) => set("location_id", e.target.value as any as number[])}
            sx={{ minWidth: 200 }}>
            {locationOptions.map((l) => <MenuItem key={l.id} value={l.id}>{l.label}</MenuItem>)}
          </TextField>
        </Stack>
      </Collapse>

      {chips.length > 0 && (
        <Box mt={1.5}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Active filters:</Typography>
          {chips.map((c) => (
            <Chip key={c.key} size="small" label={c.label} onDelete={c.onDelete}
              sx={{ mr: 0.5, mb: 0.5 }} />
          ))}
        </Box>
      )}
    </Card>
  );
}

export { defaultFilter };
